import {
  ListGroup, Row, Col, Button
} from "react-bootstrap";
import { useStateMachine } from "little-state-machine";
import { updateSelectedUser } from "actions/updateSelectedUser";
import { IRequest } from "interfaces/models/IRequest";
import { UserIcon } from "components/UserIcon/UserIcon";
import { useNavigate } from "react-router-dom";
import { CheckLg, X } from "react-bootstrap-icons";
import "./RequestItem.scss";
import { mapRequestToUserApplicationDetails } from "mappers/mapRequestToApplicationDetails/mapRequestToUserApplicationDetails";
import { mapRequestToUser } from "mappers/mapRequestToUser/mapRequestToUser";
import { updateSelectedUserApplicationDetails } from "actions/updateSelectedUserApplicationDetails";
import { updateAssignWhitespacePermissionsRequest } from "actions/updateAssignWhitespacePermissionsRequest";
import { format } from "date-fns";
import { enGB } from "date-fns/locale";
import { useAmplifyREST } from "hooks/useAmplifyREST";
import { useEffect } from "react";
import { ApplicationType } from "interfaces/models/ApplicationType";
import { updateAssignHwrcPermissionsRequest } from "actions/updateAssignHwrcPermissionsRequest";

interface RequestItemProps {
  request : IRequest;
  requestIndex: number;
  handleIsSending: (isSending : boolean) => void;
  handleHasErrored: (errors : string[]) => void;
  handleDeny: () => void;
}

const RequestItem = ({
  request, requestIndex, handleIsSending, handleHasErrored, handleDeny, ...props
} : RequestItemProps) => {
  const {
    isSending: approveIsSending, errors: approveErrors, sendRequest: approveSendRequest, statusCode: approveStatusCode
  } = useAmplifyREST();
  const {
    isSending: denyIsSending, errors: denyErrors, sendRequest: denySendRequest, statusCode: denyStatusCode
  } = useAmplifyREST();

  const isSending = approveIsSending || denyIsSending;
  const errors = approveErrors || denyErrors;

  const userToApplicationUrl = `users/${encodeURIComponent(request.userId)}/apps/${encodeURIComponent(request.applicationId)}`;

  const navigate = useNavigate();
  const { actions } = useStateMachine({
    updateSelectedUser,
    updateSelectedUserApplicationDetails,
    updateAssignWhitespacePermissionsRequest,
    updateAssignHwrcPermissionsRequest
  });

  const handleApproveClick = () => {
    approveSendRequest(userToApplicationUrl, "PUT");
  };

  useEffect(() => {
    if (approveStatusCode === 204) {
      actions.updateSelectedUser(mapRequestToUser(request));
      actions.updateSelectedUserApplicationDetails(mapRequestToUserApplicationDetails(request));
      actions.updateAssignWhitespacePermissionsRequest(undefined);
      actions.updateAssignHwrcPermissionsRequest(undefined);

      let url : string = "";
      switch (request.applicationType) {
        case ApplicationType.HWRC: {
          url = "/requestsassignuserhwrcroles";
          break;
        }
        case ApplicationType.Whitespace: {
          url = "/requestsassignuserwhitespaceroles";
          break;
        }
        default: {
          url = "/unknown";
          break;
        }
      }
      navigate(url);
    } else if (approveStatusCode !== 0) {
      handleHasErrored(approveErrors!);
    }
  }, [approveStatusCode]);

  const handleDenyClick = () => {
    denySendRequest(userToApplicationUrl, "DELETE");
  };

  useEffect(() => {
    if (denyStatusCode === 204) {
      handleDeny();
    } else if (denyStatusCode !== 0) {
      handleHasErrored(denyErrors!);
    }
  }, [denyStatusCode]);

  return (
    <ListGroup.Item {...props}>
      {isSending && <span className="d-none" role="status" />}
      <Row className="align-items-center request-item" data-testid="requestItemRow">
        <Col xs={5} md={4} lg={5}>
          <Row className="align-items-center">
            <Col xs={1}>
              <UserIcon isFederatedUser={request.isFederatedUser} isPortalAdminUser={request.isPortalAdmin} />
            </Col>
            <Col xs={11}>
              <div className="username" data-testid="username">{request.username}</div>
              <div className="emailAddress" data-testid="emailAddress">{request.emailAddress}</div>
            </Col>
          </Row>

        </Col>
        <Col xs={7} md={6} lg={4} data-testid="applicationGroup">{request.site}</Col>
        <Col xs={2} className="d-none d-lg-block" data-testid="timestamp">{format(new Date(request.timestamp), "dd/MM/yyyy HH:mm:ss", { locale: enGB })}</Col>
        <Col xs={12} xl={1} className="d-flex justify-content-end">
          <Button
            data-testid={`btnApprove_${requestIndex}`}
            variant="primary"
            className="right-margin-small"
            aria-label="Approve User"
            disabled={isSending}
            onClick={() => handleApproveClick()}
          >
            <CheckLg size={22} />
          </Button>
          <Button
            data-testid={`btnDeny_${requestIndex}`}
            variant="primary"
            className="right-margin-small"
            aria-label="Deny User"
            disabled={isSending}
            onClick={() => handleDenyClick()}
          >
            <X size={22} />
          </Button>
        </Col>
      </Row>
    </ListGroup.Item>
  );
};

export default RequestItem;
